import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import {
  getProfileSettings,
  updateProfileSettings,
  getPerformerProfile,
  updatePerformerProfile,
  getCredits,
  addProfileCredit,
  deleteProfileCredit,
  toggleFeaturedCredit,
  getSkills,
  addSkill,
  deleteSkill,
  getMedia,
  addMedia,
  updateMedia,
  deleteMedia,
  getContactLinks,
  addContactLink,
  deleteContactLink,
  getProfessions,
  addProfession,
  deleteProfession,
  getUserLocation,
  updateUserLocation,
  uploadFile,
  getMediaUrl,
  scanResume,
  getSlug,
  updateSlug,
  checkSlugAvailable,
} from '../services/api';
import PhotoPositioner from '../components/PhotoPositioner';
import DesignTab from '../components/ProfileSettings/DesignTab';
import HomeTab from '../components/ProfileSettings/HomeTab';
import AboutTab from '../components/ProfileSettings/AboutTab';
import ResumeTab from '../components/ProfileSettings/ResumeTab';
import MediaTab from '../components/ProfileSettings/MediaTab';
import { useFileUpload } from '../hooks/useFileUpload';
import ContactTab from '../components/ProfileSettings/ContactTab';
import ShowcaseQR from '../components/ShowcaseQR';
import '../components/ShowcaseQR.css';
import '../styles/ProfileThemes.css';
import './ProfileSettings.css';

function ProfileSettings() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('design');
  const [message, setMessage] = useState({ type: '', text: '' });

  // Profile Settings (visual customization)
  const [profileSettings, setProfileSettings] = useState({
    theme: 'bold',
    layout_mode: 'scroll',
    hero_alignment: 'center',
    headshot_shape: 'circle',
    primary_color: '#D8A45D',
    secondary_color: '#F9EFE6',
    accent_color: '#222222',
    background_color: '#FAFAFA',
    text_color: '#222222',
    heading_font: 'Inter',
    body_font: 'Inter',
    heading_font_size: '32',
    body_font_size: '16',
    gallery_grid_config: null,
  });

  // Performer Profile (core info)
  const [performerProfile, setPerformerProfile] = useState({
    display_name: '',
    tagline: '',
    bio: '',
    job_title: '',
    union_status: '',
    representation: '',
    height: '',
    hair_color: '',
    eye_color: '',
    age_range: '',
    website: '',
  });

  // Credits
  const [credits, setCredits] = useState([]);
  const [newCredit, setNewCredit] = useState({
    category: 'Theatre',
    role: '',
    production: '',
    company: '',
    director: '',
    year: '',
  });

  // Skills
  const [skills, setSkills] = useState([]);
  const [newSkill, setNewSkill] = useState({ category: 'General', skill: '', proficiency: '' });

  // Media
  const [mediaItems, setMediaItems] = useState([]);
  const [newMedia, setNewMedia] = useState({
    media_type: 'video',
    title: '',
    url: '',
    embed_url: '',
  });

  // Contact Links
  const [contactLinks, setContactLinks] = useState([]);
  const [newLink, setNewLink] = useState({ link_type: 'website', label: '', url: '' });

  // Location
  const [location, setLocation] = useState('');

  // Resume PDF
  const [resumePdfName, setResumePdfName] = useState('');

  // Resume Scanner
  const [resumeScanning, setResumeScanning] = useState(false);
  const [scannedCredits, setScannedCredits] = useState(null);
  const [resumeType, setResumeType] = useState('performer');

  // Professions
  const [professions, setProfessions] = useState([]);
  const [newProfession, setNewProfession] = useState('');

  // Photos (slot-based)
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(null);
  const [repositioningPhoto, setRepositioningPhoto] = useState(null);

  // Slug (custom URL)
  const [currentSlug, setCurrentSlug] = useState('');
  const [slugInput, setSlugInput] = useState('');
  const [slugStatus, setSlugStatus] = useState(null); // null, 'checking', 'available', 'taken', 'reserved', 'error'
  const [slugSaving, setSlugSaving] = useState(false);
  const slugCheckTimerRef = React.useRef(null);

  // Unsaved changes tracking
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const savedStateRef = useRef(null);

  // Copy URL state
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [showQR, setShowQR] = useState(false);

  const handleCopyUrl = async () => {
    if (!currentSlug) return;
    const fullUrl = `${window.location.origin}/${currentSlug}`;
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
    } catch (e) {
      // Fallback for browsers without clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = fullUrl;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopiedUrl(true);
        setTimeout(() => setCopiedUrl(false), 2000);
      } catch {}
      document.body.removeChild(textArea);
    }
  };

  // Mark dirty when settings or profile change after initial load
  const markDirty = useCallback(() => {
    if (!loading && savedStateRef.current) setHasUnsavedChanges(true);
  }, [loading]);

  useEffect(() => {
    markDirty();
  }, [profileSettings, performerProfile, location]);

  // beforeunload warning
  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);

  // Load all data on mount
  useEffect(() => {
    if (user?.id) loadAllData();
  }, [user]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [
        settingsRes,
        performerRes,
        creditsRes,
        skillsRes,
        mediaRes,
        linksRes,
        professionsRes,
        locationRes,
      ] = await Promise.all([
        getProfileSettings(user.id),
        getPerformerProfile(user.id),
        getCredits(user.id),
        getSkills(user.id),
        getMedia(user.id),
        getContactLinks(user.id),
        getProfessions(user.id),
        getUserLocation(user.id),
      ]);
      if (settingsRes.data) setProfileSettings((prev) => ({ ...prev, ...settingsRes.data }));
      if (performerRes.data) {
        setPerformerProfile((prev) => ({ ...prev, ...performerRes.data }));
        if (performerRes.data.resume_pdf_url) {
          const url = performerRes.data.resume_pdf_url;
          setResumePdfName(url.split('/').pop() || 'Resume.pdf');
        }
      }
      if (creditsRes.data) setCredits(Array.isArray(creditsRes.data) ? creditsRes.data : []);
      if (skillsRes.data) setSkills(Array.isArray(skillsRes.data) ? skillsRes.data : []);
      if (mediaRes.data) {
        const mediaData = Array.isArray(mediaRes.data) ? mediaRes.data : [];
        setPhotos(mediaData.filter((m) => m.media_type === 'image'));
        setMediaItems(mediaData.filter((m) => m.media_type !== 'image'));
      }
      if (linksRes.data) setContactLinks(Array.isArray(linksRes.data) ? linksRes.data : []);
      if (professionsRes.data) setProfessions(professionsRes.data);
      if (locationRes.data?.location) setLocation(locationRes.data.location);

      // Load slug
      try {
        const slugRes = await getSlug();
        const slug = slugRes.data?.slug || '';
        setCurrentSlug(slug);
        setSlugInput(slug);
      } catch (e) {
        console.error('Error loading slug:', e);
      }
    } catch (error) {
      console.error('Error loading profile data:', error);
      showMessage('error', 'Failed to load profile data');
    }
    setLoading(false);
    // Snapshot saved state for dirty tracking
    setTimeout(() => {
      savedStateRef.current = true;
    }, 100);
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  // Slug handlers
  const handleSlugChange = (value) => {
    const cleaned = value
      .toLowerCase()
      .replace(/[^a-z0-9_-]/g, '-')
      .replace(/-+/g, '-');
    setSlugInput(cleaned);
    setSlugStatus('checking');
    if (slugCheckTimerRef.current) clearTimeout(slugCheckTimerRef.current);
    if (!cleaned || cleaned.length < 2) {
      setSlugStatus(null);
      return;
    }
    if (cleaned === currentSlug) {
      setSlugStatus(null);
      return;
    }
    slugCheckTimerRef.current = setTimeout(async () => {
      try {
        const res = await checkSlugAvailable(cleaned);
        const data = res.data;
        if (data.available) {
          setSlugStatus('available');
        } else if (data.reason === 'reserved') {
          setSlugStatus('reserved');
        } else {
          setSlugStatus('taken');
        }
      } catch (e) {
        setSlugStatus('error');
      }
    }, 400);
  };

  const handleSlugSave = async () => {
    if (!slugInput || slugInput.length < 2 || slugInput === currentSlug) return;
    setSlugSaving(true);
    try {
      await updateSlug(slugInput);
      setCurrentSlug(slugInput);
      setSlugStatus(null);
      showMessage('success', 'Your showcase URL has been updated!');
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Failed to update URL');
    }
    setSlugSaving(false);
  };

  const getEmbedUrl = (url) => {
    if (!url) return '';
    const ytRegExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const ytMatch = url.match(ytRegExp);
    if (ytMatch && ytMatch[2].length === 11) return `https://www.youtube.com/embed/${ytMatch[2]}`;
    const vimeoRegExp = /(?:vimeo\.com\/(?:video\/|channels\/\w+\/|groups\/\w+\/videos\/|)?)(\d+)/;
    const vimeoMatch = url.match(vimeoRegExp);
    if (vimeoMatch && vimeoMatch[1]) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    return url;
  };

  // Save handlers
  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await updateProfileSettings(profileSettings);
      setHasUnsavedChanges(false);
      showMessage('success', 'Theme settings saved!');
    } catch (error) {
      showMessage('error', 'Failed to save settings');
    }
    setSaving(false);
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      await Promise.all([
        updateProfileSettings(profileSettings),
        updatePerformerProfile(performerProfile),
        updateUserLocation(location),
      ]);
      setHasUnsavedChanges(false);
      showMessage('success', 'All changes saved!');
    } catch (error) {
      showMessage('error', 'Failed to save some changes');
    }
    setSaving(false);
  };

  // Credit handlers
  const handleAddCredit = async () => {
    if (!newCredit.role || !newCredit.production) {
      showMessage('error', 'Role and production are required');
      return;
    }
    try {
      const res = await addProfileCredit(newCredit);
      setCredits([...credits, { ...newCredit, id: res.data.id }]);
      setNewCredit({
        category: 'Theatre',
        role: '',
        production: '',
        company: '',
        director: '',
        year: '',
      });
      showMessage('success', 'Credit added!');
    } catch (error) {
      showMessage('error', 'Failed to add credit');
    }
  };

  const handleDeleteCredit = async (id) => {
    try {
      await deleteProfileCredit(id);
      setCredits(credits.filter((c) => c.id !== id));
      showMessage('success', 'Credit removed');
    } catch (error) {
      showMessage('error', 'Failed to delete credit');
    }
  };

  const handleToggleFeatured = async (id) => {
    try {
      const res = await toggleFeaturedCredit(id);
      const result = res.data;
      setCredits(
        credits.map((c) => (c.id === id ? { ...c, is_featured: result.is_featured ? 1 : 0 } : c))
      );
      showMessage(
        'success',
        result.is_featured ? 'Credit featured! It will show on your card.' : 'Credit unfeatured'
      );
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Failed to update credit');
    }
  };

  // Skill handlers
  const handleAddSkill = async () => {
    if (!newSkill.skill) {
      showMessage('error', 'Skill name is required');
      return;
    }
    try {
      const res = await addSkill(newSkill);
      setSkills([...skills, { ...newSkill, id: res.data.id }]);
      setNewSkill({ category: 'General', skill: '', proficiency: '' });
      showMessage('success', 'Skill added!');
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Failed to add skill');
    }
  };

  const handleDeleteSkill = async (id) => {
    try {
      await deleteSkill(id);
      setSkills(skills.filter((s) => s.id !== id));
      showMessage('success', 'Skill removed');
    } catch (error) {
      showMessage('error', 'Failed to delete skill');
    }
  };

  // Media handlers
  const handleAddMedia = async () => {
    if (!newMedia.url) {
      showMessage('error', 'URL is required');
      return;
    }
    const mediaToAdd = { ...newMedia, embed_url: getEmbedUrl(newMedia.url) };
    try {
      const res = await addMedia(mediaToAdd);
      setMediaItems([...mediaItems, { ...mediaToAdd, id: res.data.id }]);
      setNewMedia({ media_type: 'video', title: '', url: '', embed_url: '' });
      showMessage('success', 'Media added!');
    } catch (error) {
      showMessage('error', 'Failed to add media');
    }
  };

  const handleDeleteMedia = async (id) => {
    try {
      await deleteMedia(id);
      setMediaItems(mediaItems.filter((m) => m.id !== id));
      showMessage('success', 'Media removed');
    } catch (error) {
      showMessage('error', 'Failed to delete media');
    }
  };

  // Contact link handlers
  const handleAddLink = async () => {
    if (!newLink.label || !newLink.url) {
      showMessage('error', 'Label and value are required');
      return;
    }
    try {
      const res = await addContactLink(newLink);
      const id = res.data?.id;
      setContactLinks([...contactLinks, { ...newLink, id }]);
      setNewLink({ link_type: 'website', label: '', url: '' });
      showMessage('success', 'Added!');
    } catch (error) {
      console.error('Add link error:', error?.response?.data || error);
      showMessage('error', error?.response?.data?.error || 'Failed to add');
    }
  };

  const handleDeleteLink = async (id) => {
    try {
      await deleteContactLink(id);
      setContactLinks(contactLinks.filter((l) => l.id !== id));
      showMessage('success', 'Link removed');
    } catch (error) {
      showMessage('error', 'Failed to delete link');
    }
  };

  // Profession handlers
  const handleAddProfession = async () => {
    if (!newProfession.trim()) {
      showMessage('error', 'Profession is required');
      return;
    }
    try {
      const res = await addProfession({ profession: newProfession.trim() });
      setProfessions((prev) => [
        ...prev,
        {
          profession: newProfession.trim(),
          id: res.data.id,
          is_primary: professions.length === 0 ? 1 : 0,
        },
      ]);
      setNewProfession('');
      showMessage('success', 'Profession added!');
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Failed to add profession');
    }
  };

  const handleDeleteProfession = async (id) => {
    try {
      await deleteProfession(id);
      setProfessions(professions.filter((p) => p.id !== id));
      showMessage('success', 'Profession removed');
    } catch (error) {
      showMessage('error', 'Failed to remove profession');
    }
  };

  // Photo handlers
  const handlePhotoUpload = async (files, slot) => {
    if (!files || files.length === 0) return;
    if (files.length > 5) {
      showMessage(
        'error',
        `You can only upload up to 5 photos at once. You selected ${files.length}.`
      );
      return;
    }
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const uploadRes = await uploadFile(file);
        const url = uploadRes.data.url;
        const mediaRes = await addMedia({ media_type: 'image', url, slot: slot || null });
        setPhotos((prev) => [
          ...prev,
          { id: mediaRes.data.id, media_type: 'image', url, slot: slot || null },
        ]);
      }
      showMessage('success', slot ? 'Photos uploaded!' : 'Photos added to library!');
    } catch (error) {
      console.error('Photo upload error:', error?.response?.data || error.message);
      showMessage('error', error?.response?.data?.error || 'Failed to upload photos');
    }
    setUploading(false);
  };

  const handleLibraryUpload = (files) => handlePhotoUpload(files, null);

  const handleAssignSlot = async (photoId, slot) => {
    try {
      const photo = photos.find((p) => p.id === photoId);
      if (!photo) return;
      await updateMedia(photoId, { ...photo, slot });
      setPhotos((prev) => prev.map((p) => (p.id === photoId ? { ...p, slot } : p)));
    } catch (error) {
      showMessage('error', 'Failed to assign photo');
    }
  };

  const handleUnassignSlot = async (photoId) => {
    try {
      const photo = photos.find((p) => p.id === photoId);
      if (!photo) return;
      await updateMedia(photoId, { ...photo, slot: null });
      setPhotos((prev) => prev.map((p) => (p.id === photoId ? { ...p, slot: null } : p)));
    } catch (error) {
      showMessage('error', 'Failed to unassign photo');
    }
  };

  const handleDeletePhoto = async (photoId) => {
    try {
      await deleteMedia(photoId);
      setPhotos((prev) => prev.filter((p) => p.id !== photoId));
      showMessage('success', 'Photo removed');
    } catch (error) {
      showMessage('error', 'Failed to delete photo');
    }
  };

  const handleRepositionSave = async (x, y, zoom) => {
    if (!repositioningPhoto) return;
    try {
      await updateMedia(repositioningPhoto.id, { position_x: x, position_y: y, zoom });
      setPhotos((prev) =>
        prev.map((p) =>
          p.id === repositioningPhoto.id ? { ...p, position_x: x, position_y: y, zoom } : p
        )
      );
      setRepositioningPhoto(null);
      showMessage('success', 'Photo position saved!');
    } catch (error) {
      showMessage('error', 'Failed to save position');
    }
  };

  const getPhotoGridClass = (count) => {
    if (count <= 1) return 'photo-grid-1';
    if (count === 2) return 'photo-grid-2';
    if (count === 3) return 'photo-grid-3';
    return 'photo-grid-4';
  };

  const SLOT_LABELS = { headshot: 'Hero', about: 'About', gallery: 'Gallery' };

  // Shared photo picker/slot rendering
  const renderPhotoPicker = (slotName, maxPhotos = 4) => {
    if (pickerOpen !== slotName) return null;
    const slotPhotos = photos.filter((p) => p.slot === slotName);
    const isGallery = slotName === 'gallery';
    return (
      <div className="photo-picker-inline">
        {photos.length > 0 && (
          <>
            <div className="photo-picker-section-label">Choose from your library</div>
            <div className="photo-picker-library">
              {photos.map((photo) => {
                const isAssignedHere = photo.slot === slotName;
                const isAssignedElsewhere = photo.slot && photo.slot !== slotName;
                const atMax = !isGallery && slotPhotos.length >= maxPhotos && !isAssignedHere;
                return (
                  <div
                    key={photo.id}
                    className={`photo-picker-thumb${isAssignedHere ? ' selected' : ''}${isAssignedElsewhere ? ' dimmed' : ''}${atMax ? ' disabled' : ''}`}
                    onClick={() => {
                      if (atMax) return;
                      isAssignedHere
                        ? handleUnassignSlot(photo.id)
                        : handleAssignSlot(photo.id, slotName);
                    }}
                  >
                    <img src={getMediaUrl(photo.url)} alt="" />
                    {isAssignedHere && (
                      <div className="photo-picker-check">
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#fff"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </div>
                    )}
                    {isAssignedElsewhere && (
                      <div className="photo-picker-badge">
                        {SLOT_LABELS[photo.slot] || photo.slot}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="photo-picker-divider">
              <span>or upload new</span>
            </div>
          </>
        )}
        <div className="photo-picker-upload">
          <label
            className="photo-picker-upload-area"
            onDragOver={(e) => {
              e.preventDefault();
              e.currentTarget.classList.add('drag-over');
            }}
            onDragLeave={(e) => {
              e.currentTarget.classList.remove('drag-over');
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.currentTarget.classList.remove('drag-over');
              handlePhotoUpload(e.dataTransfer.files, slotName);
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <span>{uploading ? 'Uploading...' : 'Upload & add to this section'}</span>
            <small>Drag & drop or click to browse</small>
            <input
              type="file"
              accept="image/*"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => handlePhotoUpload(e.target.files, slotName)}
              disabled={uploading}
            />
          </label>
        </div>
        <button className="photo-picker-close" onClick={() => setPickerOpen(null)}>
          Done
        </button>
      </div>
    );
  };

  const renderPhotoSlot = (slotName, label, hint, maxPhotos = 4) => {
    const slotPhotos = photos.filter((p) => p.slot === slotName);
    const isGallery = slotName === 'gallery';
    return (
      <div className="settings-card">
        <div className="settings-card-header">
          <h2>{label}</h2>
          {hint && <p>{hint}</p>}
        </div>
        {slotPhotos.length > 0 && (
          <>
            <div className={`photo-slot-grid ${getPhotoGridClass(slotPhotos.length)}`}>
              {slotPhotos.map((photo) => (
                <div key={photo.id} className="photo-slot-item-wrap">
                  <div className="photo-slot-item">
                    <img
                      src={getMediaUrl(photo.url)}
                      alt=""
                      style={{
                        objectPosition: `${photo.position_x ?? 50}% ${photo.position_y ?? 50}%`,
                        transform:
                          (photo.zoom ?? 100) > 100
                            ? `scale(${(photo.zoom ?? 100) / 100})`
                            : undefined,
                        transformOrigin: `${photo.position_x ?? 50}% ${photo.position_y ?? 50}%`,
                      }}
                    />
                    <button
                      className="photo-slot-remove"
                      onClick={() => handleUnassignSlot(photo.id)}
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </div>
                  <button
                    className="photo-slot-reposition-btn"
                    onClick={() => setRepositioningPhoto(photo)}
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="5 9 2 12 5 15" />
                      <polyline points="9 5 12 2 15 5" />
                      <polyline points="15 19 12 22 9 19" />
                      <polyline points="19 9 22 12 19 15" />
                      <line x1="2" y1="12" x2="22" y2="12" />
                      <line x1="12" y1="2" x2="12" y2="22" />
                    </svg>
                    Reposition
                  </button>
                </div>
              ))}
            </div>
            {repositioningPhoto && slotPhotos.find((p) => p.id === repositioningPhoto.id) && (
              <PhotoPositioner
                photoUrl={getMediaUrl(repositioningPhoto.url)}
                positionX={repositioningPhoto.position_x ?? 50}
                positionY={repositioningPhoto.position_y ?? 50}
                zoom={repositioningPhoto.zoom ?? 100}
                onSave={handleRepositionSave}
                onCancel={() => setRepositioningPhoto(null)}
              />
            )}
          </>
        )}
        <button
          className="btn-add"
          onClick={() => setPickerOpen(pickerOpen === slotName ? null : slotName)}
          style={{ marginTop: slotPhotos.length > 0 ? '12px' : '0' }}
        >
          {pickerOpen === slotName
            ? 'Close'
            : `Choose Photo${isGallery ? 's' : ''}${slotPhotos.length > 0 && !isGallery ? ` (${slotPhotos.length}/${maxPhotos})` : ''}`}
        </button>
        {renderPhotoPicker(slotName, maxPhotos)}
      </div>
    );
  };

  const handlePreview = () => {
    handleSaveAll().then(() => navigate('/showcase'));
  };

  const isResumePdfFile = (file) => {
    const fileName = file?.name?.toLowerCase() || '';
    return file?.type === 'application/pdf' || fileName.endsWith('.pdf');
  };

  // Resume handlers — canonical example of useFileUpload (ARCH-042). The
  // hook owns the file-input click + upload roundtrip; onSuccess receives
  // the resolved URL and the original File so we can drive both the
  // visual rename (resumePdfName) and the persistence call.
  const resumeUpload = useFileUpload({
    accept: '.pdf',
    validateFile: (file) => isResumePdfFile(file) || 'Please select a PDF file',
    onValidationError: (error) => showMessage('error', error.message || 'Please select a PDF file'),
    onSuccess: async (url, file) => {
      setResumePdfName(file.name);
      setPerformerProfile((prev) => ({ ...prev, resume_pdf_url: url }));
      try {
        await updatePerformerProfile({ ...performerProfile, resume_pdf_url: url });
        showMessage('success', 'Resume PDF uploaded!');
      } catch (error) {
        showMessage('error', 'Failed to upload resume PDF');
      }
    },
    onError: () => showMessage('error', 'Failed to upload resume PDF'),
  });
  const handleUploadResume = resumeUpload.onFileChange;

  const handleRemoveResume = async () => {
    setResumePdfName('');
    setPerformerProfile((prev) => ({ ...prev, resume_pdf_url: null }));
    await updatePerformerProfile({ ...performerProfile, resume_pdf_url: null });
    showMessage('success', 'Resume PDF removed');
  };

  const handleResumeScan = async (file) => {
    setResumeScanning(true);
    setScannedCredits(null);
    try {
      const res = await scanResume(file, resumeType);
      const credits = res.data.credits || [];
      const scannedSkills = res.data.skills || [];
      setScannedCredits(credits);

      // Auto-save scanned skills
      if (scannedSkills.length > 0) {
        let savedCount = 0;
        for (const s of scannedSkills) {
          try {
            await addSkill({ category: s.category || 'General', skill: s.skill });
            savedCount++;
          } catch (e) {
            /* skip duplicates */
          }
        }
        if (savedCount > 0) {
          showMessage(
            'success',
            `Added ${savedCount} skill${savedCount !== 1 ? 's' : ''} from resume`
          );
          // Reload skills
          const skillsRes = await getSkills(user.id);
          if (skillsRes.data) setSkills(skillsRes.data);
        }
      }

      if (credits.length === 0 && scannedSkills.length === 0)
        showMessage('error', 'No credits or skills found. Try a clearer image or PDF.');
    } catch (error) {
      console.error('Resume scan error:', error);
      showMessage('error', error.response?.data?.error || 'Failed to scan resume');
    }
    setResumeScanning(false);
  };

  const handleEditScannedCredit = (index, field, value) => {
    setScannedCredits((prev) => prev.map((c, i) => (i === index ? { ...c, [field]: value } : c)));
  };

  const handleRemoveScannedCredit = (index) => {
    if (index === 'all') {
      setScannedCredits(null);
      return;
    }
    setScannedCredits((prev) => prev.filter((_, i) => i !== index));
  };

  const handleConfirmScannedCredits = async () => {
    if (!scannedCredits || scannedCredits.length === 0 || saving) return;
    setSaving(true);
    const creditsToAdd = [...scannedCredits];
    setScannedCredits(null);
    try {
      const newCredits = [];
      for (const credit of creditsToAdd) {
        const creditData = {
          category: credit.category || 'Theatre',
          production: credit.production || '',
          role: credit.role || '',
          company: credit.company || '',
          director: credit.director || '',
          year: credit.year || '',
        };
        const res = await addProfileCredit(creditData);
        newCredits.push({ ...creditData, id: res.data.id });
      }
      setCredits((prev) => [...prev, ...newCredits]);
      showMessage('success', `${newCredits.length} credits added from resume!`);
    } catch (error) {
      showMessage('error', 'Failed to save some credits');
    }
    setSaving(false);
  };

  const hiddenSections = profileSettings.hidden_sections || [];
  const handleToggleSection = (sectionId) => {
    const updated = hiddenSections.includes(sectionId)
      ? hiddenSections.filter((s) => s !== sectionId)
      : [...hiddenSections, sectionId];
    setProfileSettings({ ...profileSettings, hidden_sections: updated });
  };

  if (loading) {
    return (
      <div className="settings-page">
        <div className="settings-container">
          <div className="loading-state">Loading your profile...</div>
        </div>
      </div>
    );
  }

  const sidebarTabs = [
    {
      id: 'design',
      label: 'Design',
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M12 19l7-7 3 3-7 7-3-3z" />
          <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" />
          <path d="M2 2l7.586 7.586" />
          <circle cx="11" cy="11" r="2" />
        </svg>
      ),
    },
    {
      id: 'home',
      label: 'Home',
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          <polyline points="9 22 9 12 15 12 15 22" />
        </svg>
      ),
    },
    {
      id: 'about',
      label: 'About',
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      ),
    },
    {
      id: 'media',
      label: 'Media/Gallery',
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polygon points="23 7 16 12 23 17 23 7" />
          <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
        </svg>
      ),
    },
    {
      id: 'resume',
      label: 'Resume',
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
        </svg>
      ),
    },
    {
      id: 'contact',
      label: 'Contact',
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
        </svg>
      ),
    },
  ];

  return (
    <div className="settings-page">
      {message.text && <div className={`settings-message ${message.type}`}>{message.text}</div>}
      <div className="settings-layout">
        <aside className="settings-sidebar">
          <div className="settings-sidebar-header">
            <h1>Your Portfolio.</h1>
          </div>
          <nav className="settings-sidebar-nav">
            {sidebarTabs.map((tab) => (
              <button
                key={tab.id}
                className={`settings-sidebar-item${activeTab === tab.id ? ' active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
          <div className="settings-sidebar-footer">
            {hasUnsavedChanges && (
              <div className="unsaved-indicator">
                <span className="unsaved-dot" />
                <span>Unsaved changes</span>
              </div>
            )}
            <button className="btn-preview" onClick={handlePreview}>
              Save & Preview
            </button>
            <button className="btn-secondary" onClick={() => navigate(-1)}>
              Back
            </button>
          </div>
        </aside>

        <main className="settings-main">
          {activeTab === 'design' && (
            <DesignTab
              profileSettings={profileSettings}
              setProfileSettings={setProfileSettings}
              photos={photos}
              uploading={uploading}
              handleLibraryUpload={handleLibraryUpload}
              handleDeletePhoto={handleDeletePhoto}
              handleSaveSettings={handleSaveSettings}
              saving={saving}
            />
          )}
          {activeTab === 'home' && (
            <HomeTab
              performerProfile={performerProfile}
              setPerformerProfile={setPerformerProfile}
              profileSettings={profileSettings}
              setProfileSettings={setProfileSettings}
              professions={professions}
              newProfession={newProfession}
              setNewProfession={setNewProfession}
              handleAddProfession={handleAddProfession}
              handleDeleteProfession={handleDeleteProfession}
              renderPhotoSlot={renderPhotoSlot}
              handleSaveAll={handleSaveAll}
              saving={saving}
            />
          )}
          {activeTab === 'about' && (
            <AboutTab
              performerProfile={performerProfile}
              setPerformerProfile={setPerformerProfile}
              profileSettings={profileSettings}
              setProfileSettings={setProfileSettings}
              location={location}
              setLocation={setLocation}
              hiddenSections={hiddenSections}
              handleToggleSection={handleToggleSection}
              renderPhotoSlot={renderPhotoSlot}
              handleSaveAll={handleSaveAll}
              saving={saving}
            />
          )}
          {activeTab === 'resume' && (
            <ResumeTab
              hiddenSections={hiddenSections}
              handleToggleSection={handleToggleSection}
              resumeScanning={resumeScanning}
              scannedCredits={scannedCredits}
              resumeType={resumeType}
              setResumeType={setResumeType}
              handleResumeScan={handleResumeScan}
              handleEditScannedCredit={handleEditScannedCredit}
              handleRemoveScannedCredit={handleRemoveScannedCredit}
              handleConfirmScannedCredits={handleConfirmScannedCredits}
              credits={credits}
              newCredit={newCredit}
              setNewCredit={setNewCredit}
              handleAddCredit={handleAddCredit}
              handleDeleteCredit={handleDeleteCredit}
              handleToggleFeatured={handleToggleFeatured}
              skills={skills}
              newSkill={newSkill}
              setNewSkill={setNewSkill}
              handleAddSkill={handleAddSkill}
              handleDeleteSkill={handleDeleteSkill}
              resumePdfName={resumePdfName}
              handleUploadResume={handleUploadResume}
              handleRemoveResume={handleRemoveResume}
              handleSaveAll={handleSaveAll}
              saving={saving}
            />
          )}
          {activeTab === 'media' && (
            <MediaTab
              profileSettings={profileSettings}
              setProfileSettings={setProfileSettings}
              hiddenSections={hiddenSections}
              handleToggleSection={handleToggleSection}
              mediaItems={mediaItems}
              newMedia={newMedia}
              setNewMedia={setNewMedia}
              handleAddMedia={handleAddMedia}
              handleDeleteMedia={handleDeleteMedia}
              photos={photos}
              setPhotos={setPhotos}
              renderPhotoSlot={renderPhotoSlot}
              showMessage={showMessage}
            />
          )}
          {activeTab === 'contact' && (
            <ContactTab
              hiddenSections={hiddenSections}
              handleToggleSection={handleToggleSection}
              contactLinks={contactLinks}
              newLink={newLink}
              setNewLink={setNewLink}
              handleAddLink={handleAddLink}
              handleDeleteLink={handleDeleteLink}
              contactBlurb={performerProfile.contact_blurb}
              setContactBlurb={(val) =>
                setPerformerProfile((prev) => ({ ...prev, contact_blurb: val }))
              }
              handleSaveAll={handleSaveAll}
            />
          )}
        </main>

        <aside className="settings-info-card">
          <h2>Your Spotlight.</h2>
          <p>Your own website in the industry — one link to share your work, credits, and story.</p>

          <div className="slug-editor">
            <label className="slug-label">Your public URL</label>
            <div className="slug-input-row">
              <span className="slug-prefix">showcase-online.com/</span>
              <input
                type="text"
                value={slugInput}
                onChange={(e) => handleSlugChange(e.target.value)}
                placeholder="your-name"
                className="slug-input"
                maxLength={60}
              />
            </div>
            {slugStatus === 'available' && (
              <span className="slug-status available">Available!</span>
            )}
            {slugStatus === 'taken' && <span className="slug-status taken">Already taken</span>}
            {slugStatus === 'reserved' && (
              <span className="slug-status taken">This URL is reserved</span>
            )}
            {slugStatus === 'checking' && <span className="slug-status checking">Checking...</span>}
            {slugInput && slugInput !== currentSlug && slugStatus === 'available' && (
              <button className="slug-save-btn" onClick={handleSlugSave} disabled={slugSaving}>
                {slugSaving ? 'Saving...' : 'Save URL'}
              </button>
            )}
            {currentSlug && (
              <>
                <div className="slug-actions">
                  <button
                    type="button"
                    className={`slug-copy-btn${copiedUrl ? ' copied' : ''}`}
                    onClick={handleCopyUrl}
                  >
                    {copiedUrl ? (
                      <>
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        Copied!
                      </>
                    ) : (
                      <>
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>
                        Copy link
                      </>
                    )}
                  </button>
                  <a
                    href={`/${currentSlug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="slug-preview-link"
                  >
                    View live →
                  </a>
                </div>
                <button type="button" className="slug-qr-btn" onClick={() => setShowQR(true)}>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="3" width="7" height="7" />
                    <rect x="14" y="3" width="7" height="7" />
                    <rect x="3" y="14" width="7" height="7" />
                    <rect x="14" y="14" width="3" height="3" />
                    <line x1="21" y1="14" x2="21" y2="14.01" />
                    <line x1="21" y1="21" x2="21" y2="21.01" />
                    <line x1="17" y1="21" x2="17" y2="21.01" />
                    <line x1="21" y1="17" x2="21" y2="17.01" />
                  </svg>
                  Show QR code
                </button>
              </>
            )}
          </div>

          {showQR && currentSlug && (
            <ShowcaseQR
              slug={currentSlug}
              displayName={user?.full_name || user?.username}
              onClose={() => setShowQR(false)}
            />
          )}
        </aside>
      </div>
    </div>
  );
}

export default ProfileSettings;
